<svelte:options tag={null} />

<script lang="ts">
  import { tick } from 'svelte';
  import { get_current_component } from 'svelte/internal';
  import { EventHandler } from '../../utilities/EventHandler';
  import { clickOutside } from '../../utilities/ClickOutside';
  import type { ListboxItems } from '../listbox/listbox.types';
  import type { SelectSize } from '../select/select.types';

  import Tag from '../tag/Tag.nested.svelte';
  import Loader from '../loader/Loader.nested.svelte';
  import Listbox from '../listbox/Listbox.nested.svelte';

  export let maxwidth = '100%';
  export let tagid: string;
  export let taglabel = '';
  export let placeholder = '-- Placeholder --';
  export let disabled = false;
  export let size: SelectSize = 'm';
  export let isvalid: boolean;
  export let isinvalid: boolean;
  export let loading = false;
  export let items: string;
  export let value: any;
  export let open = false;
  export let multiple = false;
  export let emptysearchlabel = 'No results found';
  export let datatextexpr = 'label';
  export let datavalueexpr = 'value';
  export let nosort = false;
  export let clearfield = false;
  export let layertitle = 'Title layer';
  export let showmoretagslabel = 'Show more';
  export let maxnumbertoshowtags = 12;
  export let alltags = false;

  const component: any = get_current_component();
  const uuid = Math.random();
  let eventHandler = new EventHandler();
  let tag: any;
  let tagWidth = '0px';
  let tagValue: any;
  let buttonvalue: string;
  let sortedListItems: any;
  let isfiltered = false;
  let selectedItems: ListboxItems[] = [];
  let openLayer: any = null;

  $: parsedItems = items ? (JSON.parse(items) as ListboxItems[]) : [];
  $: localItems = parsedItems;
  $: listboxValue = !value && multiple ? [] : value ? JSON.parse(value) : value;
  $: setTagLabel = () => {
    return listboxValue.length.toString() + ' ' + taglabel;
  };
  $: watchListboxValue(listboxValue);
  $: setTagWidth(tagValue);
  $: openState = open;
  $: {
    const eventName = openState ? 'open' : 'close';
    eventHandler.dispatch(eventName, openState);
  }
  $: attributes = { ...$$restProps };
  $: delete attributes.class;
  function clearListbox() {
    listboxValue = multiple ? [] : '';
    closeListBox();
    eventHandler.dispatch('clear', true);
  }

  function removeTagSelected(itemSelectedId: any) {
    listboxValue = listboxValue.filter(
      (value: any) => value !== itemSelectedId,
    );
    eventHandler.dispatch('onchange', listboxValue);
  }

  function openLayerFn() {
    openLayer = true;
  }

  function closeLayerFn() {
    openLayer = null;
  }

  function closeListBox() {
    openState = false;

    if (multiple && !nosort) {
      sortItems();
    } else {
      localItems = parsedItems;
    }
  }

  function onChange() {
    eventHandler.dispatch('onchange', listboxValue);

    if (!multiple) {
      closeListBox();
    }
  }

  function getSelectedItems(val?: any) {
    const value = val ? val : listboxValue;

    const selectedItems = parsedItems.filter((item: ListboxItems) => {
      return multiple
        ? value.includes(item[datavalueexpr])
        : value === item[datavalueexpr];
    });

    return selectedItems;
  }

  function sortItems() {
    sortedListItems = parsedItems;
    const selectedItems = getSelectedItems();

    sortedListItems.sort((a: any, b: any) => {
      const hasItemA = selectedItems.includes(a);
      const hasItemB = selectedItems.includes(b);

      if (hasItemA === hasItemB) {
        return a[datavalueexpr] - b[datavalueexpr];
      } else if (hasItemA < hasItemB) {
        return 1;
      } else {
        return -1;
      }
    });

    localItems = sortedListItems;
  }

  function onScrollEnd() {
    eventHandler.dispatch('scrollend', true);
  }

  // Watch Functions
  async function watchListboxValue(value: any) {
    await tick();
    selectedItems = getSelectedItems(value);
    const seletedLabels = selectedItems.map((item) => item[datatextexpr]);

    buttonvalue = seletedLabels.join(', ');

    if (multiple) {
      tagValue = value;
    }
  }

  async function setTagWidth(tagValue: any) {
    await tick();

    tagWidth =
      tag && tagValue && tagValue.length
        ? `${
            component.shadowRoot.querySelector('.mc-tag-removable')
              .clientWidth + 8
          }px`
        : '0px';
  }
</script>

<div
  class="mc-dropdown"
  class:mc-dropdown--multi={multiple}
  style="--dropdown-tag-width: {tagWidth}; --dropdown-width: {maxwidth};"
  use:clickOutside
  on:outclick={closeListBox}
  {...attributes}
>
  <!--  TAG REMOVABLE -->
  {#if multiple && listboxValue.length > 0 && !alltags}
    <Tag
      id={tagid ? tagid : `autocompleteTag-${uuid}`}
      class="mc-dropdown__tag"
      label={setTagLabel()}
      size="s"
      type="removable"
      dark={false}
      disabled={false}
      href=""
      name=""
      selected={false}
      bind:this={tag}
      on:tag-removed={clearListbox}
    />
  {/if}

  <!-- LOADER -->
  {#if loading}
    <Loader size="s" theme="primary" text="" class="mc-dropdown__loader" />
  {/if}

  <!-- BUTTON -->
  <button
    type="button"
    class="mc-select mc-dropdown__trigger"
    class:is-open={openState}
    class:is-valid={isvalid}
    class:is-invalid={isinvalid}
    class:mc-select--s={size === 's'}
    {disabled}
    on:click={() => (openState = !openState)}
  >
    {buttonvalue ? buttonvalue : placeholder}
    {#if !multiple && buttonvalue && clearfield}
      <button
        class="mc-dropdown__clear"
        type="button"
        on:click|stopPropagation={() => {
          clearListbox();
        }}
      >
        <navigation-control-tag--cross-24px />
      </button>
    {/if}
  </button>

  <!-- LISTBOX -->
  <Listbox
    items={JSON.stringify(localItems)}
    open={openState}
    {multiple}
    {isfiltered}
    {emptysearchlabel}
    {maxwidth}
    {datatextexpr}
    {datavalueexpr}
    bind:value={listboxValue}
    on:change={onChange}
    on:scrollend={onScrollEnd}
  />

  {#if multiple && alltags}
    <div class="tags-selected">
      {#each selectedItems as selectedItem, index}
        {#if index < maxnumbertoshowtags}
          <div class="tag-selected">
            <m-tag
              id={selectedItem.id}
              label={selectedItem.label}
              size="s"
              type="removable"
              on:tag-removed={() => removeTagSelected(selectedItem.id)}
            />
          </div>
        {/if}
      {/each}
      {#if listboxValue.length > maxnumbertoshowtags}
        <div class="tag-selected">
          <m-tag
            id="id-tag"
            label="({listboxValue.length}) {showmoretagslabel} "
            size="s"
            type="link"
            on:tag-selected={() => openLayerFn()}
          />
        </div>
      {/if}
    </div>
  {/if}

  <span class="mc-dropdown__tag" style="display:none;" />
  <span class="mc-dropdown__loader" style="display:none;" />
  <span class="mc-text-input mc-dropdown__trigger" style="display:none;" />

  <div>
    <m-layer
      title={layertitle}
      layertitletag="h3"
      isopen={openLayer}
      on:layer-closed={() => closeLayerFn()}
    >
      <small slot="content">
        <div class="tags-selected">
          {#each selectedItems as selectedItem}
            <div class="tag-selected">
              <m-tag
                id={selectedItem.id}
                label={selectedItem.label}
                size="s"
                type="removable"
                on:tag-removed={() => removeTagSelected(selectedItem.id)}
              />
            </div>
          {/each}
        </div>
      </small>
    </m-layer>
  </div>
</div>

<style lang="scss">
  @import '@mozaic-ds/styles/settings-tools/all-settings';
  @import '@mozaic-ds/styles/components/c.select';
  @import '@mozaic-ds/styles/components/c.dropdown';

  // Dropdown
  // NOTE: These styles with the "!important" allow to overload the edge effects
  // due to the scoping applied by Svelte on the CSS selectors
  .mc-dropdown {
    &__tag {
      position: absolute !important; // due to the Svelte CSS scope
    }

    &--multi {
      .mc-dropdown__trigger {
        padding-left: calc($mu075 + var(--dropdown-tag-width)) !important;
      }
    }
  }

  // Selected Tags
  .tags-selected {
    display: flex;
    flex-wrap: wrap;
  }

  .tag-selected {
    margin-top: 1em;
    margin-left: 5px;
    margin-right: 5px;
  }
</style>
